// "What Merlin remembers": the reader's own memory bank, which shapes every
// watch of theirs (kanban 001b6e2b — it outlived the personal digest). Merlin
// writes it from the chat; here the reader adds a standing ask or forgets one.
// An admin can create the bank for a reader who has none.
import { useCallback, useEffect, useState } from 'react';
import { readerMemoryApi, type ReaderMemoryView } from '../../services/personDigest';
import styles from '../../pages/BriefingPage.module.css';

const SOURCE_LABEL: Record<string, string> = {
  chat: 'told Merlin',
  portal: 'added here',
  admin: 'added by admin',
};

export interface ReaderMemoryPanelProps {
  /** The reader's first name, for the copy. */
  readerName: string;
  /** The signed-in user's id (the admin action provisions this login). */
  userId: string | null;
  isAdmin: boolean;
  /** Bumped by the page after a chat turn (Merlin may have recorded an ask). */
  refreshKey?: number;
}

export function ReaderMemoryPanel({ readerName, userId, isAdmin, refreshKey = 0 }: ReaderMemoryPanelProps) {
  const [view, setView] = useState<ReaderMemoryView | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [newAsk, setNewAsk] = useState('');

  const load = useCallback(async () => {
    try {
      setView(await readerMemoryApi.me());
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const run = useCallback(
    async (fn: () => Promise<unknown>, failure: string) => {
      setBusy(true);
      setNote(null);
      try {
        await fn();
        await load();
      } catch {
        setNote(failure);
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  const remember = () => {
    const text = newAsk.trim();
    if (!text) return;
    void run(async () => {
      await readerMemoryApi.remember(text);
      setNewAsk('');
    }, 'Could not save that.');
  };

  if (failed) {
    return (
      <section className={styles.asks} aria-label="What Merlin remembers">
        <h2>What Merlin remembers</h2>
        <div className={styles.asksEmpty}>Could not load what Merlin remembers.</div>
      </section>
    );
  }
  if (!view) return null;

  return (
    <section className={styles.asks} aria-label="What Merlin remembers">
      <h2>What Merlin remembers</h2>
      <p className={styles.asksHint}>Applies to every watch of yours, your daily briefing included.</p>
      {note && <div className={styles.asksEmpty}>{note}</div>}
      {!view.memory_available ? (
        <div className={styles.asksEmpty}>
          {readerName}&apos;s memory bank is not set up here yet, so Merlin cannot remember anything
          between readouts.{' '}
          {isAdmin && userId ? (
            <button
              type="button"
              className={styles.askBtn}
              onClick={() =>
                void run(
                  () => readerMemoryApi.provision(userId),
                  'Could not create the memory bank — is the Knowledge Bank reachable?',
                )
              }
              disabled={busy}
              data-action="reader_memory_provision"
              data-outcome="request"
            >
              Create memory bank
            </button>
          ) : (
            'An admin can create it from this page.'
          )}
        </div>
      ) : (
        <>
          {view.memory.length === 0 ? (
            <div className={styles.asksEmpty}>Nothing yet — tell Merlin in the chat, or add an ask below.</div>
          ) : (
            <ul className={styles.askList}>
              {view.memory.map((m) => (
                <li key={m.id} className={styles.askItem}>
                  <span className={styles.askText}>{m.text}</span>
                  <span className={styles.askSource}>{SOURCE_LABEL[m.source] ?? m.source}</span>
                  <button
                    type="button"
                    className={styles.retireBtn}
                    onClick={() => void run(() => readerMemoryApi.forget(m.id), 'Could not forget that entry.')}
                    disabled={busy}
                    title="Stop applying this"
                    aria-label={`Forget: ${m.text}`}
                    data-action="reader_memory_forget"
                    data-target-memory-id={m.id}
                    data-outcome="request"
                  >
                    Forget
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className={styles.askAdd}>
            <input
              className={styles.askInput}
              placeholder="Add a standing ask…"
              value={newAsk}
              onChange={(e) => setNewAsk(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') remember();
              }}
              maxLength={500}
              aria-label="New standing ask"
            />
            <button
              type="button"
              className={styles.askBtn}
              onClick={remember}
              disabled={busy || !newAsk.trim()}
              data-action="reader_memory_remember"
              data-outcome="request"
            >
              Remember
            </button>
          </div>
        </>
      )}
    </section>
  );
}
